import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/schedora/StatusBadge";
import { cn } from "@/lib/utils";
import { buildAppointmentView, minutesToTimeString } from "@/lib/booking";
import { useApp, useOwnerSnapshot } from "@/context/AppContext";

const hours = [9, 10, 11, 12, 13, 14, 15, 16, 17];

function addDays(date: string, amount: number) {
  const next = new Date(`${date}T12:00:00`);
  next.setDate(next.getDate() + amount);
  return next.toISOString().slice(0, 10);
}

export default function OwnerCalendar() {
  const [view, setView] = useState<"day" | "week">("day");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const snapshot = useOwnerSnapshot(selectedDate);
  const { state, updateAppointmentStatus } = useApp();
  const todayAppts = snapshot?.views.filter((appointment) => appointment.date === selectedDate) ?? [];
  const weekDates = Array.from({ length: 5 }, (_, index) => addDays(selectedDate, index));

  const groupedWeek = useMemo(() => {
    return weekDates.map((date) => ({
      date,
      items: (snapshot?.appointments ?? [])
        .filter((appointment) => appointment.date === date)
        .map((appointment) =>
          buildAppointmentView({
            appointment,
            clients: state.clients,
            services: state.services,
            staff: state.staff,
          }),
        ),
    }));
  }, [snapshot?.appointments, state.clients, state.services, state.staff, weekDates]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Calendar</h1>
          <p className="text-sm text-muted-foreground">Manage your daily flow and appointment statuses</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="rounded-lg border bg-card px-3 py-2 text-sm"
          />
          <div className="flex gap-1 rounded-lg border p-0.5">
            <Button size="sm" variant={view === "day" ? "default" : "ghost"} onClick={() => setView("day")}>Day</Button>
            <Button size="sm" variant={view === "week" ? "default" : "ghost"} onClick={() => setView("week")}>Week</Button>
          </div>
        </div>
      </div>

      {view === "day" ? (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="grid grid-cols-[80px_1fr] divide-x">
            <div className="divide-y">
              {hours.map((hour) => (
                <div key={hour} className="h-24 flex items-start justify-end pr-3 pt-2 text-xs text-muted-foreground">
                  {minutesToTimeString(hour * 60)}
                </div>
              ))}
            </div>
            <div className="relative divide-y">
              {hours.map((hour) => <div key={hour} className="h-24" />)}
              {todayAppts.map((appointment) => {
                const top = ((appointment.startMinutes - 9 * 60) / 60) * 96;
                const height = (appointment.duration / 60) * 96;
                const statusColor =
                  appointment.status === "confirmed"
                    ? "bg-primary/10 border-primary/30"
                    : appointment.status === "completed"
                      ? "bg-success/10 border-success/30"
                      : appointment.status === "cancelled"
                        ? "bg-destructive/10 border-destructive/20"
                        : "bg-warning/10 border-warning/30";

                return (
                  <div
                    key={appointment.id}
                    className={cn("absolute left-2 right-2 rounded-lg border p-2", statusColor)}
                    style={{ top: `${top}px`, height: `${Math.max(height, 64)}px` }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate">{appointment.serviceName}</p>
                        <p className="text-xs text-muted-foreground truncate">{appointment.clientName} · {appointment.staffName}</p>
                        <p className="text-xs text-muted-foreground mt-1">{appointment.timeLabel}</p>
                      </div>
                      <StatusBadge status={appointment.status} />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => updateAppointmentStatus(appointment.id, "confirmed")}>Confirm</Button>
                      <Button size="sm" variant="outline" onClick={() => updateAppointmentStatus(appointment.id, "completed")}>Complete</Button>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => updateAppointmentStatus(appointment.id, "cancelled")}>Cancel</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border bg-card p-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {groupedWeek.map((day) => (
              <div key={day.date}>
                <p className="text-sm font-semibold text-center mb-3">
                  {new Date(`${day.date}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" })}
                </p>
                <div className="space-y-2">
                  {day.items.map((appointment) => (
                    <div key={appointment.id} className="rounded-lg border bg-secondary/30 p-2">
                      <p className="text-xs font-medium truncate">{appointment.timeLabel}</p>
                      <p className="text-xs text-muted-foreground truncate">{appointment.serviceName}</p>
                    </div>
                  ))}
                  {day.items.length === 0 && (
                    <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground text-center">
                      Open
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
